import InfoIcon from "@/assets/colorable/info.svg?react";
import LongRightArrowIcon from "@/assets/colorable/long_right_arrow.svg?react";
import Button from "@/components/Common/Button";
import Modal from "@/components/Common/Modal";
import TextButton from "@/components/Common/TextButton";
import MiniPlanInfoCard from "@/components/plans/MiniPlanInfoCard";
import usePackageLimits from "@/hooks/usePackageLimits";
import usePackages from "@/hooks/usePackages";
import {usePayment} from "@/hooks/usePayment";
import {usePriceFormat} from "@/hooks/usePriceFormat";
import {VAT_PERCENTAGE} from "@/store/constants";
import {isAddTiktokToPlanModalOpenAtom, isTiktokUpgradePendingAtom} from "@/store/jotaiAtoms";
import type {TCurrency} from "@/store/types";
import useUserStore from "@/store/user";
import {useSetAtom} from "jotai";
import {type FC} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {useIntercom} from "react-use-intercom";

const UNDO_WINDOW_MS = 5000;

const AddTiktokToPlanModal: FC = () => {
  const {t} = useTranslation(["search_visibility", "common"]);
  const setIsOpen = useSetAtom(isAddTiktokToPlanModalOpenAtom);
  const setIsUpgradePending = useSetAtom(isTiktokUpgradePendingAtom);
  const {showMessages} = useIntercom();
  const formatPrice = usePriceFormat();
  const {upgradePlan} = usePayment();
  const subscription = useUserStore(state => state.user.package_subscription);
  const {name, packageCategory, packageType, pricePerMonth, pricePerYear, currency} = usePackageLimits();
  const {packagesData: bothPackages} = usePackages(currency as TCurrency, "BOTH");
  const isYearly = packageType === "YEARLY";
  const isSwissBrand = currency === "CHF";

  const planWithTiktok = bothPackages?.find(p => p.name === name && p.package_category === "BOTH");
  const newPricePerYear = planWithTiktok?.price_per_year ?? pricePerYear ?? 0;
  const newPricePerMonth = planWithTiktok?.price_per_month ?? pricePerMonth ?? 0;
  const priceDifference = isYearly ? newPricePerYear - (pricePerYear ?? 0) : newPricePerMonth - (pricePerMonth ?? 0);

  const handleClose = () => setIsOpen(false);

  return (
    <Modal
      backgroundClassName="max-w-135"
      closeButtonClassName="mt-5 self-start"
      customBottomRow={
        <div className="flex flex-col gap-2.5 p-5">
          <Button
            buttonStyle="Gradient"
            className="h-10 w-full sm:h-15"
            onClick={() => {
              if (!planWithTiktok || !packageType || !subscription?.id) return;
              setIsOpen(false);
              setIsUpgradePending(true);

              let isUndone = false;
              toast.info(
                <div className="flex w-full items-center justify-between gap-3">
                  <span className="text-neutral-black text-xs">{t("plan_updated_tiktok_added")}</span>
                  <Button
                    buttonStyle="White"
                    className="h-fit min-h-8 shrink-0 p-3 py-1 text-sm"
                    onClick={() => {
                      isUndone = true;
                      toast.dismiss();
                    }}>
                    {t("common:undo")}
                  </Button>
                </div>,
                {
                  autoClose: UNDO_WINDOW_MS,
                  closeOnClick: false,
                  pauseOnHover: true,
                  onClose: async () => {
                    if (isUndone) {
                      setIsUpgradePending(false);
                      return;
                    }
                    try {
                      await upgradePlan({
                        package_id: planWithTiktok.id,
                        package_type: packageType,
                        subscriptionId: subscription.id!,
                      });
                    } finally {
                      setIsUpgradePending(false);
                    }
                  },
                  className:
                    "mb-20 p-0 bg-white sm:w-112.5 w-[90vw] rounded-lg border-neutral-white-dark min-h-15 border px-4",
                },
              );
            }}>
            {t("add_tiktok")}
          </Button>
          <div className="flex items-start gap-2.5">
            <TextButton className="h-8 px-8 text-sm" onClick={handleClose} text={t("common:close")} />
            <TextButton className="h-8 px-8 text-sm" onClick={() => showMessages()} text={t("common:needHelp")} />
          </div>
        </div>
      }
      customTitleRow={
        <div className="flex w-full items-start justify-between p-5">
          <div className="flex flex-col items-start justify-center gap-1 sm:gap-3">
            <div className="bg-primary-powder flex h-5 items-center justify-center rounded-full px-2">
              <span className="text-2xs bg-linear-(--primary-dark-gradient) bg-clip-text font-semibold text-transparent uppercase">
                {t("plan_upgrade")}
              </span>
            </div>
            <span className="text-neutral-black text-base leading-2.5 font-semibold sm:text-xl sm:leading-3.5">
              {t("add_tiktok_to_your_plan")}
            </span>
            <span className="text-neutral-black text-xs leading-3 sm:text-sm sm:leading-5">
              {t("to_access_search_visibility_add_tiktok")}
            </span>
          </div>
        </div>
      }
      onClose={handleClose}>
      <div className="flex w-full flex-col items-center gap-2 opacity-80">
        <span className="text-neutral-grey-dark text-xs leading-2.5">{t("old_plan")}</span>
        <MiniPlanInfoCard
          chipsClassName="flex-wrap gap-y-1"
          className="border-neutral-grey-light w-full border opacity-70"
          name={name}
          packageCategory={packageCategory}
          packageType={packageType}
          pricePerMonth={pricePerMonth}
          pricePerYear={pricePerYear}
        />
      </div>
      <div className="flex items-center justify-center">
        <LongRightArrowIcon className="text-primary-dark-solid h-5 rotate-90" />
      </div>
      <div className="flex w-full flex-col items-center gap-2">
        <span className="text-neutral-grey-dark text-xs leading-2.5">{t("new_plan")}</span>
        <MiniPlanInfoCard
          chipsClassName="flex-wrap gap-y-1"
          className="bg-neutral-white-light w-full"
          highlightTiktok
          name={name}
          packageCategory="BOTH"
          packageType={packageType}
          priceDifference={priceDifference}
          pricePerMonth={newPricePerMonth}
          pricePerYear={newPricePerYear}
        />
      </div>
      <div className="bg-primary-powder border-primary-light-solid flex items-center gap-2.5 rounded-md border px-3 py-2 sm:px-5 sm:py-3.5">
        <InfoIcon className="text-primary-medium-solid size-3.5 shrink-0" />
        <span className="text-neutral-black text-xs leading-4">
          {t("benefits_immediately_difference", {amount: formatPrice(priceDifference)})}
        </span>
      </div>
      <span className="text-neutral-grey-dark text-sm">
        {isSwissBrand
          ? t("common:pricesDontIncludeVATSwitzerland", {vatPercentage: VAT_PERCENTAGE})
          : t("common:pricesDontIncludeVAT")}
      </span>
    </Modal>
  );
};

export default AddTiktokToPlanModal;
